/**
 * Smart 404 redirect.
 *
 * Reads the failed URL and first checks it against a small table of intent rules
 * (legacy account, downloads, add-to-cart, mission-statement, free-download and
 * article-hub links). If no rule matches, it fuzzy-matches against the
 * product/author search index. Either way it offers an auto-redirect to the
 * destination (or homepage) with a visible countdown the visitor can cancel.
 *
 * Suppresses itself on common bot-probe paths so we don't help scanners
 * (the intent rules run before that check).
 */
(function () {
  'use strict';

  // Paths that look like bot probes — render a quiet 404, no smart UI
  const BOT_PROBE_PATTERNS = [
    /^\/wp-/i,
    /^\/wordpress/i,
    /^\/phpmyadmin/i,
    /^\/pma\b/i,
    /^\/\.env/i,
    /^\/\.git/i,
    /^\/\.htaccess/i,
    /^\/admin\b/i,
    /^\/cgi-bin/i,
    /^\/server-status/i,
    /^\/(backup|config|database|sql)\.?/i,
    /\.(php|asp|aspx|jsp|cgi|pl|sh|env|sql|bak|old|swp)(\?|$)/i,
    /\/(xmlrpc|wlwmanifest|setup-config)/i,
  ];

  const COUNTDOWN_SECONDS = 5;
  const HIGH_CONFIDENCE = 0.55;  // Above this, redirect to the match
  const LOW_CONFIDENCE  = 0.30;  // Above this, show as suggestion only

  const SITE_ORIGIN = 'https://www.nightingale.com';
  const MYMISSION_URL = 'https://mymission.ai/?utm_source=ncsite&utm_medium=redirect&utm_campaign=legacy_pms';
  const PRODUCT_HANDLE_RE = /^[a-z0-9-]+$/;
  // An author `lnk` is honored only when it is a same-origin path: one leading /,
  // never // or /\, and no whitespace, backslash or ASCII control character (URL
  // parsing deletes tab/LF/CR, so `/<TAB>/host` would otherwise become //host).
  const SAME_ORIGIN_PATH_RE = /^\/(?![\/\\])[^\s\\\x00-\x1f\x7f]*$/;
  const MISSION_SEGMENT_RE = /^(missions?[\s_-]*s?[rt]at|missionbuild|missionst|mission_select|example-mission-statement|tmission)/;
  const ACCOUNT_DOWNLOAD_PAGES = ['my-mp3s', 'my-account'];

  /*
   * INTENT RULES — the allowlist contract.
   *
   * Every destination below is a literal same-origin path, a /products/<handle>
   * path whose handle comes from the site's own search index (and matches
   * ^[a-z0-9-]+$), or the one literal mymission.ai URL. URL content (a legacy IDN,
   * a path segment) may only SELECT a rule or an index entry; it never becomes part
   * of a destination. resolveIntentRule() is the only evaluator; init() calls it
   * before the bot-probe check and before the fuzzy match.
   *
   * Docs: docs/_DOC_THEME_ARCHITECTURE.md § Smart 404 Page.
   * Intent invariant: shopify_integration / smart_404_redirects_allowlisted_and_rule_first.
   *
   * Each rule: `match(view)` reads the URL view built by intentView(); `url`/`label`
   * is the destination. A rule with `idnParam` needs the search index: the param's
   * leading-digit run selects a product by its `d` field, and `url` is the fallback
   * when nothing matches. Rules run in order; the first match wins.
   */
  const INTENT_RULES = [
    {
      // 1. Magento account pages (incl. /customer/account/login/referer/<b64>).
      id: 'customer-account',
      match: (v) => v.segs[0] === 'customer' && v.segs[1] === 'account',
      url: '/account',
      label: 'your account',
    },
    {
      // 2. Download links and download-account pages (not the free-download thank-you pages).
      id: 'my-downloads',
      match: (v) =>
        v.segs[0] === 'downloadable' ||
        (v.segs[0] === 'accountpages' && ACCOUNT_DOWNLOAD_PAGES.indexOf(v.segs[1]) !== -1) ||
        v.segs[0] === 'mydownloads' ||
        v.segs[0] === 'my-downloads' ||
        (v.segs[0] === 'secure' &&
          v.segs.slice(1).some((s) => s.indexOf('download') !== -1) &&
          !/thank-you$/.test(v.segs[v.segs.length - 1])),
      url: '/pages/my-downloads',
      label: 'My Downloads',
    },
    {
      // 3a. Legacy add-to-cart link with a product IDN in ?add=.
      id: 'shopping-cart-add',
      match: (v) => v.segs[0] === 'accountpages' && v.segs[1] === 'shopping-cart' &&
        paramIgnoreCase(v.params, 'add') !== null,
      idnParam: 'add',
      url: '/collections/all',
      label: 'all programs',
    },
    {
      // 3b. Legacy product page with a product IDN in ?productidn=.
      id: 'prod-detail',
      match: (v) => v.segs[0] === 'prod_detail.aspx' && paramIgnoreCase(v.params, 'productidn') !== null,
      idnParam: 'productidn',
      url: '/collections/all',
      label: 'all programs',
    },
    {
      // 3c. Legacy add-to-cart endpoint (no IDN).
      id: 'nccustom-addtocart',
      match: (v) => v.segs[0] === 'nccustom-addtocart',
      url: '/collections/all',
      label: 'all programs',
    },
    {
      // 4. Mission-statement links (first segment only, so a deeper product/article
      //    path that merely contains "mission-statement" stays on this site).
      id: 'mission-statement',
      match: (v) => MISSION_SEGMENT_RE.test(safeDecode(v.rawSegs[0] || '').trim().toLowerCase()),
      url: MYMISSION_URL,
      label: 'mymission.ai',
    },
    {
      // 5. Free-download offers and their thank-you pages.
      id: 'free-downloads',
      match: (v) =>
        v.segs[0] === 'free-downloads' ||
        v.segs[0] === 'premium-free-downloads' ||
        (v.segs[0] === 'secure' && v.segs.length > 1 && /thank-you$/.test(v.segs[v.segs.length - 1])),
      url: '/pages/app',
      label: 'the Nightingale-Conant app',
    },
    {
      // 6. Old newsletter / article hubs.
      id: 'article-hubs',
      match: (v) => v.segs.length === 1 && /^(newsletters?|news|articles-.+)$/.test(v.segs[0]),
      url: '/blogs/articles',
      label: 'our articles',
    },
  ];
  INTENT_RULES.forEach(Object.freeze);
  Object.freeze(INTENT_RULES);

  // The only URI decode in this file: a malformed % sequence returns the raw string.
  function safeDecode(s) {
    try {
      return decodeURIComponent(s);
    } catch (e) {
      return s;  // URIError on a malformed % sequence: keep the raw text
    }
  }

  // Query-param lookup with a case-insensitive name; null when absent.
  function paramIgnoreCase(params, name) {
    for (const [key, value] of params) {
      if (key.toLowerCase() === name) return value;
    }
    return null;
  }

  function intentView(url) {
    const rawSegs = url.pathname.split('/').filter(Boolean);
    return {
      rawSegs,
      segs: rawSegs.map((s) => s.toLowerCase()),
      params: url.searchParams,
    };
  }

  // Selects an index product by a legacy IDN. Only the FULL leading-digit run of
  // 2-8 digits is used (never truncated); the first product in index order whose
  // `d` equals it wins. Returns null when nothing matches or the handle is not safe.
  function productForIdn(value, index) {
    const m = /^(\d+)/.exec(String(value));
    if (!m || m[1].length < 2 || m[1].length > 8) return null;
    const products = (index && index.products) || [];
    for (const p of products) {
      if (!p || p.d === undefined || p.d === null || String(p.d) !== m[1]) continue;
      if (typeof p.h !== 'string' || !PRODUCT_HANDLE_RE.test(p.h)) return null;
      const label = typeof p.t === 'string' && p.t ? p.t : 'this program';
      return { kind: 'intent', url: `/products/${p.h}`, label, score: 1 };
    }
    return null;
  }

  /**
   * Evaluates INTENT_RULES against a URL object (built from location.href).
   * Pure: no DOM or location reads. `index` is the parsed search index or null.
   * Returns {kind:'intent', url, label, score: 1} on a hit, {needsIndex: true}
   * when the matching rule needs the index and `index` is null, or null.
   */
  function resolveIntentRule(url, index) {
    const view = intentView(url);
    for (const rule of INTENT_RULES) {
      if (!rule.match(view)) continue;
      if (rule.idnParam) {
        if (!index) return { needsIndex: true };
        const product = productForIdn(paramIgnoreCase(view.params, rule.idnParam), index);
        if (product) return product;
      }
      return { kind: 'intent', url: rule.url, label: rule.label, score: 1 };
    }
    return null;
  }

  // The only author-destination chooser: the index's `lnk` override when it is a
  // same-origin path (regex AND parsed origin AND no // path), else /pages/<handle>.
  function authorHref(a) {
    const fallback = `/pages/${a && a.h}`;
    if (!a || typeof a.lnk !== 'string' || !SAME_ORIGIN_PATH_RE.test(a.lnk)) return fallback;
    // A string that passed the regex is a path, so this parse cannot throw. Dot
    // segments can still collapse to a // path (`/..//host`), so that is rejected too.
    const u = new URL(a.lnk, SITE_ORIGIN);
    return u.origin === SITE_ORIGIN && !u.pathname.startsWith('//') ? a.lnk : fallback;
  }

  function isBotProbe(path) {
    return BOT_PROBE_PATTERNS.some((re) => re.test(path));
  }

  function extractSlug(pathname) {
    // Strip trailing slash, take last segment, replace separators with spaces
    const cleaned = pathname.replace(/\/+$/, '');
    const segments = cleaned.split('/').filter(Boolean);
    if (!segments.length) return '';
    let slug = segments[segments.length - 1];
    // Drop file extensions and trailing UUIDs/numbers
    slug = slug.replace(/\.[a-z0-9]{1,5}$/i, '');
    slug = slug.replace(/[-_]?\d{4,}$/i, '');
    // Convert separators to spaces
    return safeDecode(slug).replace(/[-_+]/g, ' ').trim();
  }

  function bigrams(str) {
    const s = (str || '').toLowerCase().replace(/[^a-z0-9 ]/g, '');
    const set = new Set();
    for (let i = 0; i < s.length - 1; i++) set.add(s[i] + s[i + 1]);
    return set;
  }

  function bigramScore(a, b) {
    if (!a.size || !b.size) return 0;
    let hits = 0;
    for (const p of a) if (b.has(p)) hits++;
    return (2 * hits) / (a.size + b.size);
  }

  function findBestMatch(query, index) {
    if (!query || !index) return null;
    const q = query.toLowerCase().replace(/[^a-z0-9 ]/g, '');
    const qBg = bigrams(q);
    let best = null;

    function score(text, contextBoost = 0) {
      const tLower = (text || '').toLowerCase();
      const tBg = bigrams(text || '');
      let s = bigramScore(qBg, tBg);
      if (tLower.includes(q)) s += 0.4;
      if (tLower.startsWith(q)) s += 0.2;
      return s + contextBoost;
    }

    function consider(item, kind, url, label, image) {
      const s = score(label) + (kind === 'product' ? 0.05 : 0);
      if (!best || s > best.score) {
        best = { kind, url, label, image, score: s };
      }
    }

    (index.products || []).forEach((p) => {
      consider(p, 'product', `/products/${p.h}`, p.t, p.i || null);
    });
    (index.authors || []).forEach((a) => {
      consider(a, 'author', authorHref(a), a.n, null);
    });

    return best;
  }

  function buildUI(container, target) {
    const useMatch = target && target.score >= HIGH_CONFIDENCE;
    const useSuggest = target && target.score >= LOW_CONFIDENCE && !useMatch;

    const destUrl = useMatch ? target.url : '/';
    const destLabel = useMatch ? target.label : 'Home';

    const wrap = document.createElement('div');
    wrap.className = 'smart-404';
    wrap.innerHTML = `
      <div class="smart-404__inner">
        <h1 class="smart-404__title">Directing you to your page&hellip;</h1>
        ${useSuggest ? `
          <p class="smart-404__suggest">
            Did you mean
            <a href="${escapeHtml(target.url)}">${escapeHtml(target.label)}</a>?
          </p>
        ` : ''}
        <p class="smart-404__redirecting">
          Redirecting you to <a href="${escapeHtml(destUrl)}" class="smart-404__dest-link">${escapeHtml(destLabel)}</a> in
          <span class="smart-404__count" aria-live="polite">${COUNTDOWN_SECONDS}</span>s
        </p>
        <div class="smart-404__ring" aria-hidden="true">
          <svg viewBox="0 0 100 100">
            <circle class="smart-404__ring-track" cx="50" cy="50" r="45" />
            <circle class="smart-404__ring-progress" cx="50" cy="50" r="45" />
          </svg>
          <span class="smart-404__ring-num" aria-hidden="true">${COUNTDOWN_SECONDS}</span>
        </div>
        <div class="smart-404__actions">
          <button type="button" class="button button--secondary smart-404__cancel">Cancel redirect</button>
          <a href="${escapeHtml(destUrl)}" class="button">Go now</a>
        </div>
        <p class="smart-404__or">
          Or <a href="/search">search the catalog</a> &middot;
          <a href="/collections/all">browse all programs</a>
        </p>
      </div>
    `;
    // Replace existing 404 content
    container.innerHTML = '';
    container.appendChild(wrap);

    // Trigger the ring animation
    const progress = wrap.querySelector('.smart-404__ring-progress');
    progress.style.animationDuration = `${COUNTDOWN_SECONDS}s`;

    // Countdown
    let remaining = COUNTDOWN_SECONDS;
    const numEls = wrap.querySelectorAll('.smart-404__count, .smart-404__ring-num');
    let cancelled = false;
    const interval = setInterval(() => {
      remaining -= 1;
      numEls.forEach((el) => { el.textContent = remaining; });
      if (remaining <= 0) {
        clearInterval(interval);
        if (!cancelled) window.location.assign(destUrl);
      }
    }, 1000);

    wrap.querySelector('.smart-404__cancel').addEventListener('click', () => {
      cancelled = true;
      clearInterval(interval);
      wrap.querySelector('.smart-404__redirecting').textContent = 'Redirect cancelled.';
      progress.style.animationPlayState = 'paused';
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  // A rule that needs the search index: fetch it, then resolve. When the index is
  // unavailable (no data-index-url, fetch error, !r.ok, unparseable/null body, an
  // index whose products cannot be read) the rule resolves against an empty index,
  // i.e. to its no-match destination — so buildUI never receives the {needsIndex}
  // sentinel or null for such a rule. buildUI runs exactly once, after the catch,
  // so a failure inside it can never trigger a second render.
  function resolveWithIndexAndShow(container, url, indexUrl) {
    if (!indexUrl) {
      buildUI(container, resolveIntentRule(url, { products: [] }));
      return;
    }
    fetch(indexUrl)
      .then((r) => (r.ok ? r.json() : null))
      .then((idx) => (idx ? resolveIntentRule(url, idx) : resolveIntentRule(url, { products: [] })))
      .catch(() => resolveIntentRule(url, { products: [] }))
      .then((target) => buildUI(container, target));
  }

  function init() {
    const container = document.querySelector('[data-smart-404]');
    if (!container) return;

    const indexUrl = container.getAttribute('data-index-url');

    // Intent rules run FIRST — before the bot-probe check and the fuzzy match.
    const url = new URL(window.location.href);
    const intent = resolveIntentRule(url, null);
    if (intent && intent.needsIndex) {
      // Skips the bot-probe check on purpose: legacy prod_detail.aspx links are rescued.
      resolveWithIndexAndShow(container, url, indexUrl);
      return;
    }
    if (intent) {
      buildUI(container, intent);
      return;
    }

    // No rule matched: the pre-existing bot-probe -> extractSlug -> fuzzy flow.
    const path = window.location.pathname || '/';

    if (isBotProbe(path)) {
      // Quiet 404 — leave the default content as-is
      return;
    }

    if (!indexUrl) return;

    const slug = extractSlug(path);
    fetch(indexUrl)
      .then((r) => (r.ok ? r.json() : null))
      .then((idx) => {
        const match = idx && slug ? findBestMatch(slug, idx) : null;
        buildUI(container, match);
      })
      .catch(() => {
        // Fallback: still offer a graceful redirect to home
        buildUI(container, null);
      });
  }

  // Read-only test hook (tests/smart404_intent_rules.test.mjs); no behavior.
  window.NCSmart404 = Object.freeze({ resolveIntentRule, INTENT_RULES, authorHref });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
